import {
	Inject,
	Input,
	OnInit,
	ChangeDetectorRef,
	OnDestroy,
	Directive
} from '@angular/core';
import { OrderService } from '@ispm-client/modules/orders/services/order.service';
import {
	IOrder,
	IOrderDetail,
	IOrderDetailDefinition,
	IOrderManagerConfig, ISelectedOrderDefinition, PropertyToValidate
} from '@ispm-client/modules/orders/interfaces';
import { Map as immutable } from 'immutable';
import { of, combineLatest, Observable, Subject, Subscription, forkJoin } from 'rxjs';
import { auditTime, distinctUntilChanged, map, skipWhile, switchMap, take, takeUntil } from 'rxjs/operators';
import { get, set, cloneDeep, sortBy, partition, has, differenceBy, isEqual, remove, includes, isNull, isEmpty, isUndefined, findIndex, unset, keyBy, values, merge, concat } from 'lodash';
import { IWidgetItem } from '@ispm/widgets/interfaces/widget-item.interface';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { ModalLoaderNgjsService } from '@ispm/layout/services/modal-loader-ngjs.service';
import { ConfirmModalLoaderPayload } from '@ispm/layout/classes/confirm-modal-loader-payload';
import { WorkflowUtilService } from '@ispm-client/modules/aggregator/services/workflow-util.service';
import { ContextProviderService } from '@ispm-client/modules/context-provider/services/context-provider.service';
import { IEvent } from "@ispm-client/modules/events/Interfaces";
import { IWorkflowRequest } from "@ispm/workflow/interfaces/workflow-request.interface";
import { IRevision } from "@ispm/workflow/interfaces/revision.interface";
import { IWorkflowJobItem } from "@ispm/workflow/interfaces/job-item.interface";
import moment from 'moment';
import { NOTIFICATION_MESSAGE } from "@ispm-client/modules/aggregator/services/consts";
import { OrderSFTPService } from '../../services/order.sftp.service';
import {AuthenticationService} from "@ispm/user/services/authentication.service";
import {TestService} from '@ispm/dashboard/test/services/test.service';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import {ActivatedRoute} from "@angular/router";
import { RouterManagerService } from '@ispm/core/services/router-manager.service';

@Directive()
export class OrderManagerBaseComponent implements OnInit, OnDestroy, IWidgetItem {

	private contributeToTestBarButtonStatus: boolean = false;
	private orderDataSubscription: Subscription;
	private readOnlyMode: boolean = false;
	private triggerAcrRequired: boolean = false;
	private updateStatusDueDateOfDiagnosticOrder: boolean = false;

	protected onBiodesixSftpEvent$: Subject<boolean> = new Subject<boolean>();
	protected asyncdestroyer: Subject<any> = new Subject();
	protected orderSaveInProgress: boolean = false;

	public allOrderDefinitions: IOrderDetailDefinition;
	public biodesixOrderDetails$: Observable<Array<IOrderDetail>>;
	public canUserRead: boolean;
	public config: IOrderManagerConfig;
	public disableAddButton: boolean = false;
  public canDelete: boolean;
	public hasUpdatedFields: boolean = false;
	public isEditable: boolean = false;
	public loading: boolean = true;
	public ModalLoaderTunnelName: string = OrderService.ModalLoaderTunnelName;
	public orders: Array<IOrder>;
	public patientId: string;
	public selectedOrder: IOrder;
	public selectedOrderDefinition: ISelectedOrderDefinition;
	public selectedOrderDetails: Array<IOrderDetail>;
	public watchPropertyForSaveButton: Array<String> = [];
	@Input() payload: any;

	/**
	 * set order data
	 * @param widgetData
	 */
	set widgetData(widgetData: any) { }

	/** web/client/src/modules/orders
	 * set order config
	 * @param config
	 */
	set widgetConfig(config: any) {
		this.config = config.widgetPayload;
		this.readOnlyMode = get(this.config, 'readOnlyMode', false);
		if (get(this.config, 'listPanel.requiredPermissionsIds.write')) {
			this.isEditable = this.authService.doesUserHasPermission(get(this.config, 'listPanel.requiredPermissionsIds.write'));
		} else {
			this.isEditable = true;
		}
		if (this.readOnlyMode) {
			this.isEditable = false;
		}
		if (get(this.config, 'permitOnlyAssignee') && !this.readOnlyMode) {
			this._workflowUtilService.checkIfPerformer().subscribe(isPerformer => {
				this.isEditable = this.isEditable && isPerformer;
			});
		}
    this.canDelete = get(this.config, 'listPanel.canDelete', false);
	}

  constructor(
              private _orderService: OrderService,
              private changeDetector: ChangeDetectorRef,
              public notificationMessageService: NotificationMessageService,
              public notificationMessage: NOTIFICATION_MESSAGE,
              private _formHandlerService: FormHandlerService,
              private _modalLoaderService: ModalLoaderNgjsService,
              private authService: AuthenticationService,
              private _workflowUtilService: WorkflowUtilService,
              private _contextProviderService: ContextProviderService,
              protected testService: TestService,
              private _orderSFTPService: OrderSFTPService,
              private activatedRoute: ActivatedRoute,
			  private _routeManagerService: RouterManagerService
              ) {
  }

	ngOnInit() {
		this.onComponentInit();
	}

	protected onComponentInit() {
		if (get(this.config, 'listPanel.requiredPermissionsIds.read')) {
			this.canUserRead = this.authService.doesUserHasPermission(get(this.config, 'listPanel.requiredPermissionsIds.read'));
		} else {
			this.canUserRead = true;
		}
		if (!this.canUserRead) {
			this._modalLoaderService.popupModal('confirm',
				ConfirmModalLoaderPayload.builder()
					.setTitle('Access Denied')
					.setContent('You don`t currently have permission to access orders. Please contact administrator.')
					.setApproveButtonText('Close'),
				'ERROR'
			).subscribe(() => {
				window.history.back();
			});
			return;
		}
		this._contextProviderService.clearContexts(get(this.config, 'dataComposer', []).map(dc => dc.key));
		if (get(this.config, 'dataComposer')) {
			this._contextProviderService.fetchDataFromDataBindings(get(this.config, 'dataComposer')).then(data => {
				this.initOrderManagerPreparation(true);
			});
		} else {
			this.initOrderManagerPreparation();
		}
	}

	/**
	 * Init data preparation
	 */
	async initOrderManagerPreparation(hasExtraContext?: boolean) {
		if (this.isEditable) {
			//this._orderService.getAllEventAndStore(); eventmanager component is anyhow fetching all the events no need to fetch it here
			this.triggerAcrRequired = get(this.config, 'triggerAcrRequired', false);
			this.updateStatusDueDateOfDiagnosticOrder = get(this.config, 'updateStatusDueDateOfDiagnosticOrder', false);
			this.watchPropertyForSaveButton = get(this.config, 'contentPanel.watchPropertyForSaveButton', []);
		}
		this.contributeToTestBarButtonStatus = get(this.config, 'contributeToTestBarButtonStatus', false);
		if (this.contributeToTestBarButtonStatus) {
			this._orderService._aggregatorService.registerForTestBarButtonStatus(this.constructor.name);
		}
		const workflowRequest: IWorkflowRequest = await this._orderService.getActiveWorkflowRequestFromStore();
		if (get(workflowRequest, 'activeRevision') === 'None' && get(workflowRequest, 'activeJob') === 'None') {
			this.disableAddButton = true;
		}

		if (get(this.config, 'listPanel.lookupJobNameToDisableEdit')) {
			if (get(this.config, 'listPanel.lookupJobNameToDisableEdit') !== get(workflowRequest, 'latestRevisionStatus')) {
				this.disableAddButton = true;
				this.updateStatusDueDateOfDiagnosticOrder = false;
        this.canDelete = false;
			}
		}

		this.patientId = this._routeManagerService.parameters.patientId;
		await this._orderService.getAndStoreAllOrderDefinitions(get(this.config, 'testName', 'ORDER-DETAILS-TEST'));
		// LDCT databinding should be skipped while propragation to diagnosticFollowup
		let dataBinding = get(this.config, 'listPanel.dataBinding', [])
		if(get(this._routeManagerService.queryParameters,'payload') == 'diagnosticFollowup'){
			dataBinding = dataBinding.filter(obj => !obj.queryString.includes('category=ldct'));
		}
		this._orderService.getAllOrdersAndStore(
			dataBinding,
			get(this.config, 'listPanel.orderTypesToExclude', [])
		).then(() => {
			if (this.contributeToTestBarButtonStatus || this.updateStatusDueDateOfDiagnosticOrder) {
				this.observeOrderInfoFromStore();
			}
			this.changeDetector.markForCheck();
			this.loading = false;
		});
		this.orderDataSubscription = combineLatest(
			this._orderService.getAllOrderDefinition(),
			this._orderService.getAllOrders(),
			this._orderService.getAllOrderDetails()
		).pipe(distinctUntilChanged()).subscribe(([definitions, orders]) => {
			this.allOrderDefinitions = definitions;
			this.orders = sortBy(orders.valueSeq().toArray(), o => new Date(get(o, 'meta.created')));

			let biodesixOrdersData = this.orders.filter(order => order.category.code == 'biodesix');
			if (this.orders && !isEmpty(biodesixOrdersData)) {

				this.biodesixOrderDetails$ = this._orderService.getAllBiodesixOrderDetails(biodesixOrdersData).pipe(
					switchMap(orderDetails => {
						return of(orderDetails.reduce((orderDetailsValues, currentValue) => {
							orderDetailsValues.push(currentValue?.response?.entry[0]?.resource);
							return orderDetailsValues;
						}, []));
					})
				);
			}
			if (this.orders.length && this.allOrderDefinitions) {
				this.changeDetector.markForCheck();
				this._orderService.getSelectedOrder()
					.pipe(
            takeUntil(this.asyncdestroyer.asObservable()),
					  distinctUntilChanged())
					.subscribe(selectedOrder => {
						let tempPrevId = get(this.selectedOrder, 'id');
						this.selectedOrder = selectedOrder;
						if (!(this.selectedOrder)) {
							this.selectedOrder = this.orders[0];
							this._orderService.setSelectedOrder(this.selectedOrder);
						}
						if (get(this.selectedOrder, 'id') !== tempPrevId) {
							this.setOrderDefinition();
						}
					});

				this._orderService.getSelectedOrderDetails().pipe(
          takeUntil(this.asyncdestroyer.asObservable()),
				  distinctUntilChanged())
          .subscribe(orderDetails => {
					this.selectedOrderDetails = orderDetails;
				});
			}
		});
	}

	/**
	 * @method setOrderDefinition
	 * @description set order definition based on the type of the order selected
	 */
	public setOrderDefinition() {
		this._orderService.getOrderDefinition(get(this.selectedOrder, 'category.code', null))
      .pipe(takeUntil(this.asyncdestroyer.asObservable()))
			.subscribe((definition: ISelectedOrderDefinition) => {
				this.selectedOrderDefinition = cloneDeep(definition);
				this.changeDetector.markForCheck();
			});
		if (this.isEditable) {
			let firstTimeSubscription = true;
			this._orderService.getEventsData()
				.pipe(
					takeUntil(this.asyncdestroyer.asObservable()),
					auditTime(1000),
					distinctUntilChanged(),
					skipWhile(events => isEmpty(events) || this.orderSaveInProgress),
					map((eventMap: immutable<string, IEvent>) => this.getAppropriateEvent(eventMap))
				).subscribe((event: IEvent) => {
					if (!firstTimeSubscription) {
						this.checkAndUpdateOrderAsCompleted(event);
					} else {
						firstTimeSubscription = false;
						this._orderService.setLastEventProcessed(event);
					}
				});
		}
	}

	private getAppropriateEvent(eventMap: immutable<string, IEvent>): IEvent {
		let validEvent: IEvent = null;
		const events = eventMap.valueSeq().toArray()
			.filter(e => isEqual(get(this.selectedOrder, 'category.code', null), get(e, 'category.code', null)));
		if (!isEmpty(events)) {
			validEvent = events.find((event: IEvent) => event && event.approved
				&& event.selected && event.archived !== true
				&& get(event.context.find(ctx => ctx.resourceType === 'OrderInformation'), 'reference') === get(this.selectedOrder, 'id')
			);
		}
		return validEvent;
	}

	getContextForOrder(order: IOrder, resourceName: string): { reference: string, resourceType: string }[] {
		return get(order, 'context', []).filter((ctx) => get(ctx, 'resourceType', null) === resourceName);
	}

	private checkAndUpdateOrderAsCompleted(selectedEvent: IEvent) {
		try {
			const orderContext: any[] = get(this.selectedOrder, 'context', []);
			const eventIndex = findIndex(orderContext, (ctx) => ctx.resourceType === 'validatedEvent');
			let activeOrderDetails = this.selectedOrderDetails.filter(od => includes(this.selectedOrder.isActive, od.id));
			let status: { code: string, display: string };

			const lastEvent: IEvent = this._orderService.getLastEventProcessed();
			unset(lastEvent, 'resourceType');
			unset(selectedEvent, 'resourceType');

			if (selectedEvent
				&& get(this.selectedOrder, 'status.code') === 'completed' && eventIndex > -1
				&& this.checkOrderDetailsRequired(activeOrderDetails)
				&& isEqual(selectedEvent.id, orderContext[eventIndex].reference)
				&& lastEvent !== null && !isEqual(lastEvent, selectedEvent)
			) {
				this.triggerAcrValidation(this.selectedOrder);
			} else if (selectedEvent
				&& !isEqual(lastEvent, selectedEvent)
				&& this.checkOrderDetailsRequired(activeOrderDetails)) {
				status = { code: 'completed', display: 'Completed' };
				if (eventIndex > -1 && orderContext[eventIndex].reference != selectedEvent.id) {
          orderContext[eventIndex].reference = selectedEvent.id;
          this.orderSaveInProgress = true;
          this.saveOrder(true, status, true);
				} else if (eventIndex > -1 && orderContext[eventIndex].reference === selectedEvent.id){
          this.orderSaveInProgress = true;
          this.saveOrder(true, status);
        } else {
					orderContext.push({ reference: selectedEvent.id, 'resourceType': 'validatedEvent' });
					this.orderSaveInProgress = true;
					this.saveOrder(true, status);
				}

			} else {
				if (!(isEqual(lastEvent, selectedEvent))) {
					if ((activeOrderDetails.length > 0 && orderContext.length > 0) || isEmpty(selectedEvent)) {
						if(activeOrderDetails.length > 0) {
						activeOrderDetails = sortBy(activeOrderDetails, o => new Date(get(o, 'dueDate')));
						status = get(activeOrderDetails[0], 'derivedStatus');
						this.selectedOrder.dueDate = get(activeOrderDetails[0], 'dueDate');
            					}
						remove(orderContext, eventContext => get(eventContext, 'resourceType', null) === 'validatedEvent');
						this.orderSaveInProgress = true;
						this.saveOrder(true, status);
					} else {
						if (selectedEvent && eventIndex > -1 && orderContext[eventIndex].reference !== selectedEvent.id) {
							orderContext[eventIndex].reference = selectedEvent.id;
							this.orderSaveInProgress = true;
							this.saveOrder(true, status);
						} else if (selectedEvent && eventIndex > -1 && orderContext[eventIndex].reference === selectedEvent.id) {
              this.orderSaveInProgress = true;
              this.saveOrder(true, status);
            } else if (selectedEvent && eventIndex == -1) {
							orderContext.push({ reference: selectedEvent.id, 'resourceType': 'validatedEvent' });
							this.orderSaveInProgress = true;
							this.saveOrder(true, status);
						}
					}
				}
			}
			this._orderService.setLastEventProcessed(selectedEvent);
		} catch (e) {
			this.orderSaveInProgress = false;
		}
	}
	/**
	 * @method createOrder
	 * @description creates an order based on payload
	 * @param formData : payload received from the form
	 */
	async createOrder(formData: any) {
		const order = <IOrder>formData;
		const currentUser = (await this.authService).user;
		set(order, 'context', await this.getCreateOrderContext(currentUser));
		const orderTypesToExclude = get(this.config, 'listPanel.orderTypesToExclude', []);
		this._orderService.createOrder(order,
			get(this.config, 'create.backend.apiEndPoint'),
			orderTypesToExclude
		).subscribe(newOrder => {
			if (!orderTypesToExclude.includes(newOrder.category.code)) {
				this._orderService.getAllOrderDetailsAndStore([newOrder]).then(() => {
					this._orderService.setSelectedOrder(newOrder);
					this._orderService.setLoader(false);
				});
			} else { this._orderService.setLoader(false); }
			this.notificationMessageService.success(get(this.config, 'create.orderNotification.onCreate', this.notificationMessage.ORDER_CREATED_SUCCESSFULLY),10000);
		},
			() => {
        this._orderService.setLoader(false);
        this.notificationMessageService.error(get(this.config, 'create.orderNotification.onFailure', this.notificationMessage.FAILED_TO_CREATE_AN_ORDER));
			});
	}

	/**
	 * @method handleChangeSelectedOrder
	 * @description handles actions when different order is selected
	 * @param order
	 */
	handleChangeSelectedOrder(order: IOrder) {
		// If the clicked order is different from the one currently active do the process
		if (order.id !== this.selectedOrder.id) {
			this._orderService.setSelectedOrder(order);
		}
	}

	/**
	 * @method deleteOrder
	 * @description handle deleting the order by turning the isActive property to true
	 * @property isActive => true
	 * @param order
	 */
	deleteOrder(order: IOrder) {
		const promptPayload = {
			title: get(this.config, 'create.deleteModel.title', 'Delete Diagnostic Follow-up'),
			content: get(this.config, 'create.deleteModel.content', 'Are you sure want to delete the Diagnostic Follow-up?'),
			approve: 'Delete',
			decline: 'Cancel'
		};
		this.showPrompt(promptPayload).then((response: modalResponse) => {
			if (get(response, 'payload') === modalResponse.approve) {
				set(order, 'isDeleted', true);
				this._orderService.deleteOrder(order.id, order, get(this.config, 'create.backend.apiEndPoint')).subscribe(() => {
					if (order.id === this.selectedOrder.id) {
						this.selectedOrder = this.orders[0];
						this._orderService.setSelectedOrder(this.selectedOrder);
					}
					this.changeDetector.markForCheck();
					this.changeDetector.detectChanges();
					this.notificationMessageService.success(get(this.config, 'create.orderNotification.onDelete', this.notificationMessage.ORDER_DELETED_SUCCESSFULLY),10000);
				},
					() => {
						this.notificationMessageService.error(this.notificationMessage.FAILED_TO_DELETE_ORDER);
					});
			}
		});
	}

	/**
	 * @method showPrompt
	 * @param payload
	 * @description Shows the prompt popup with the configuration provided in the payload
	 */
	showPrompt(payload: any): Promise<any> {
		return this._modalLoaderService.popupModal('confirm',
			ConfirmModalLoaderPayload.builder()
				.setTitle(payload.title)
				.setContent(payload.content)
				.setApproveButtonText(payload.approve)
				.setDeclineButtonText(payload.decline),
			'ALERT'
		).take(1).toPromise();
	}

	/**
	 * @description: Handles popup to create the order
	 * @method showCreateOrderPopup
	 */
	showCreateOrderPopup() {
		this._formHandlerService.popupDynamicFormModal(
			get(this.config, 'create.title'),
			get(this.config, 'create.formId'),
			{},
			get(this.config, 'create.addBtnLabel', 'Add'),
			get(this.config, 'create.cancelBtnLabel', 'Cancel'),
			{},
			OrderService.ModalLoaderTunnelName
		).take(1).subscribe(
			data => {
				if (get(data, 'status') === modalResponse.approve) {
					this._orderService.setLoader(true);
					this.createOrder(get(data, 'payload.formData'));
				}
			}
		);
	}


	/**
	 * @method isValidData
	 * @param od
	 */
	isValidData(od: IOrderDetail): boolean {
		let validationResult: boolean = false;
		const propertiesToValidate = get(this.config, 'contentPanel.propertiesToValidate', []);
		if (!(propertiesToValidate.length)) {
			validationResult = true;
		} else {
			for (const prop of propertiesToValidate) {
				if (typeof prop === 'string') {
					if (get(od, prop, false)) {
						validationResult = true;
					}
				} else if (typeof prop === 'object' && prop as PropertyToValidate) {
          let properties: string[] = prop.validationProperties;
          properties = this.dynamicFieldValidation(od, prop, properties);
					const isOrValidationRequired = get(prop, 'isOrValidationRequired', false);
					let propertyValidationArray = [];
					properties.forEach(property => {
						if (get(od, property, false)) {
							propertyValidationArray.push(true);
						} else {
							propertyValidationArray.push(false);
						}
					});
					if (propertyValidationArray.length > 0) {
						if (isOrValidationRequired) {
							validationResult = propertyValidationArray.filter(result => result).length > 0;
						} else {
							const results: Array<boolean> = propertyValidationArray.filter(result => result);
							validationResult = results.length === propertyValidationArray.length;
						}
					}
				}
				if (validationResult) {
					break;
				}
			}
		}
		return validationResult;
	}



  /**
   * @method dynamicFieldValidation
   * @param orderDetails properties
   */
  dynamicFieldValidation(od:IOrderDetail, prop, properties) {
    if (get(od, 'drawInfo.location.display') !== 'Home Phlebotomy') {
      properties = concat(properties, prop.drawInfoValidation);
    }  if (get(od, 'clinicalFactors.ICD10.icdDiagnosis') === 'Others') {
      properties = concat(properties, prop.icdDiagnosisValidation);
    }  if (get(od, 'patientInsurance.patientInsurance.display') === 'Yes') {
      properties = concat(properties, prop.insuranceValidation);
    }  if (get(od, 'contacts.contacts.display') === 'Yes') {
      properties = concat(properties, prop.contactValidation);
    }
    return properties;
  }

	/**
	 * @method checkIfContentValid
	 * @param orderDetails
	 */
	checkIfContentValid(orderDetails: IOrderDetail[]): boolean {
		if (!orderDetails) { return true; }
		for (const od of orderDetails) {
			if (!(this.isValidData(od))) {
				return false;
			}
		}
		return true;
	}

	public async getOrderDetailData(formData): Promise<any> {
		const orderDetails: IOrderDetail[] = await this._orderService.getSelectedOrderDetails().take(1).toPromise();
		const orderContent = partition(
			get(formData, get(this.config, 'contentPanel.extractData.path'), []),
			o => has(o, 'id')
		);
		let contentToUpdate = (orderContent[0].length) ? orderContent[0] : null;
		const contentToCreate = (orderContent[1].length) ? orderContent[1] : null;
		if (contentToUpdate) {
			const difference = differenceBy(orderDetails, contentToUpdate, 'id');
			if (difference.length) {
				difference.forEach(od => od.isDeleted = true);
				contentToUpdate = contentToUpdate.concat(difference);
			}
		}
		return { contentToUpdate, contentToCreate };
	}

	/**
	 * @method onSaveOrderContent
	 * @param formData
	 */
	protected async saveOrderContent(formData: any) {
		this.hasUpdatedFields = true;
		let validOrderDetails = true;
		const data = await this.getOrderDetailData(formData);
		if (!(this.checkIfContentValid(data.contentToCreate) && this.checkIfContentValid(data.contentToUpdate))) {
			const promptPayload = {
				title: get(this.config, 'create.submitModel.title', 'Warning'),
				content: get(this.config, 'create.submitModel.content', 'Blank rows will not be saved'),
				approve: 'OK', decline: 'Cancel'
			};
			const response: modalResponse = await this.showPrompt(promptPayload);
			validOrderDetails = get(response, 'payload') === modalResponse.approve;
		}

		if (validOrderDetails) {
			this._orderService.setLoader(true);
			try {
				const data = await this.getOrderDetailData(formData);
				let created: Observable<any[]> = await this.getOrderDetailsCreateContent(data, true);
				let updated: Observable<any[]> = await this.getOrderDetailsUpdatedContent(data, true);
				this.saveOrderDetails(created, updated, get(formData, 'dueDate'));
			} catch (e) {
				this._orderService.setLoader(false);
			}
		}
	}

	/**
	 * @method onSaveOrderContent
	 * @param formData
	 */
	public onSaveOrderContent(formData: any) {
		this.saveOrderContent(formData);
	}

	protected saveCreatedAndUpdatedInfoOfOrderDetails(created: Observable<IOrderDetail[]>, updated: Observable<IOrderDetail[]>): Observable<any> {
		return forkJoin(created, updated).pipe(
			map((res) => {
				const jsonResponse = get(res[0], 'body', []);
				return jsonResponse.concat(res[1]);
			}));
	}

	protected async updateSelectedOrderDetailsByLookingDueDate(orderDueDate: string, orderDetails: any, selectedOrder: IOrder) {
		const activeOrderDetailIds = orderDetails.filter(od => od.selected).map(od => od.id);
		if (selectedOrder.dueDate != orderDueDate && isEqual(this.selectedOrder.isActive, activeOrderDetailIds)) {
			selectedOrder.dueDate = orderDueDate;
		} else {
			set(selectedOrder, 'isActive', activeOrderDetailIds);
			let lastSelectedOrderDetail = await this._orderService.getSelectedOrderDetails().pipe(distinctUntilChanged(), take(1)).toPromise();
			selectedOrder = this._orderService.findStatusAndDueDate(selectedOrder, orderDetails, lastSelectedOrderDetail);
		}
		return selectedOrder;
	}

	/**
	 * returns the newly created object field in order details model
	 * @param data holds the order details data from the store.
	 */
	protected async getOrderDetailsCreateContent(data: any, validateFormData: boolean = true): Promise<Observable<any[]>> {
		const diagnosticOrder: any = await this._orderService.getDiagnosticOrderFromStore();
		let created: Observable<any[]> = of([]).take(1);
		if (data.contentToCreate) {
			if (validateFormData) {
				data.contentToCreate = data.contentToCreate.filter(d => this.isValidData(d));
			}
			data.contentToCreate.forEach(d => {
				d.category = this.selectedOrder.category;
				d.performer = diagnosticOrder.performer;
				d.subject = diagnosticOrder.subject;
				d.organizationId = diagnosticOrder.organization;
				delete d['_df_modified'];
				d.clientIdentifier = get(this.selectedOrder, 'category.code');
				if (isEmpty(d.dateOfTest) || d.dateOfTest === "now") {
					d.dateOfTest = moment().format('DD MMM YYYY');
				} else {
					d.dateOfTest = moment(d.dateOfTest).format('DD MMM YYYY');
				}
				const originatorService = get(this.testService.testConfiguration, `originator.service`, {});
				if (originatorService && isEmpty(d.originator)) {
					let orderOriginator = get(diagnosticOrder, 'originator', {});
					if (orderOriginator.service.code == "pulmonarynoduleclinic"){
						d.originator = Object.assign(orderOriginator, { "service": originatorService });
					}
					else{
						d.originator = Object.assign(orderOriginator, { "service": orderOriginator.service });
					}
				}
				else if (originatorService && !isEmpty(d.originator)) {
					let orderOriginator = get(diagnosticOrder, 'originator', {});
					orderOriginator.type = d.originator.type;
					d.originator = Object.assign(orderOriginator, { "service": originatorService });
				}
				if (diagnosticOrder['managingFacility']) {
					d.managingFacility = diagnosticOrder['managingFacility'];
				}
			});
			if (data.contentToCreate.length) {
				created = this._orderService.createOrderDetails(data.contentToCreate, get(this.config, 'contentPanel.extractData.endPoint'));
			}
		}
		return created;
	}

	protected updateOrderInDB(selectedOrder: IOrder): Observable<IOrder> {
		return this._orderService.updateOrder(
			selectedOrder,
			get(this.config, 'create.backend.apiEndPoint', 'order')
		);
	}

	/**
	 * returns the updated object fields in order details model
	 * @param data holds the order details data from the store.
	 */
	protected async getOrderDetailsUpdatedContent(data: any, validateFormData: boolean = true): Promise<Observable<any[]>> {
		let updated: Observable<any[]> = of([]).take(1);
		if (data.contentToUpdate) {
			// For partial save no need of validadtion
			if (!validateFormData) {
				updated = data.contentToUpdate.length > 0 ? this._orderService.updateOrderDetails(data.contentToUpdate, get(this.config, 'contentPanel.extractData.endPoint')) : of([]);
				return updated;
			}
			const onlyDiffUpdate = [];
			let compareOrder;
			data.contentToUpdate.forEach(d => {
				compareOrder = this.selectedOrderDetails.find(i => i.id == d.id);
				if (!this.isValidData(d)) {
					d.isDeleted = true;
				}
				compareOrder['organizationId'] = d['organizationId'];
				compareOrder['resourceType'] = d['resourceType'];
				if (!(isEqual(compareOrder, d))) {
					onlyDiffUpdate.push(d);
				}
			});
			updated = onlyDiffUpdate.length > 0 ? this._orderService.updateOrderDetails(onlyDiffUpdate, get(this.config, 'contentPanel.extractData.endPoint')) : of([]);
		}
		return updated;
	}

	saveOrder(isEventReceived: boolean, orderStatus?: { code: string, display: string }, forceUpdate?: boolean) {
		this._orderService.setLoader(true);
		this._orderService.getSingleOrder(this.selectedOrder).subscribe(order => {
			unset(order, 'resourceType');
			unset(this.selectedOrder, 'resourceType');

			if (!isEventReceived && isEqual(order.isActive, this.selectedOrder.isActive) && isEqual(order.dueDate, this.selectedOrder.dueDate)) {
				const activeOrderDetail = this.selectedOrderDetails.find(od => od.selected === true);
				if (activeOrderDetail) {
					if (findIndex(this.selectedOrder.context, ctx => ctx.resourceType === 'validatedEvent') > -1) {
						if (get(this.selectedOrder, 'status.code') === 'completed') {
							this.setOrderDefinition();
							this._orderService.setLoader(false);
							this.triggerAcrValidation(this.selectedOrder);
						} else {
							this.selectedOrder.meta = order.meta;
							this.selectedOrder.status = { code: 'completed', display: 'Completed' };
							this.updateOrder();
						}
					} else if (isEqual(this.selectedOrder.status, activeOrderDetail.derivedStatus)) {
						this.postSavingOrderActions(order, true);
						this._orderService.setLoader(false);
					} else {
						this.selectedOrder.status = activeOrderDetail.derivedStatus;
						this.selectedOrder.meta = order.meta;
						this.selectedOrder.dueDate = order.dueDate;
						this.updateOrder();
					}
				} else {
					this.selectedOrder.meta = order.meta;
					this._orderService.setSelectedOrder(this.selectedOrder);
					this.setOrderDefinition();
					this._orderService.setLoader(false);
				}
			} else if (orderStatus) {
				this.selectedOrder.meta = order.meta;
				if (orderStatus && (!isEqual(get(this.selectedOrder, 'status'), orderStatus) || forceUpdate)) {
					set(this.selectedOrder, 'status', orderStatus);
					this.updateOrder(true);
				} else {
					this._orderService.setLoader(false);
				}
			} else if (!isEqual(this.selectedOrder, order)) {
				this.selectedOrder.meta = order.meta;
				this.updateOrder();
			} else {
				this._orderService.setLoader(false);
			}
		});
	}

	updateOrder(avoidRefresh?: boolean) {
		this._orderService.setLoader(true);
		this.updateOrderInDB(this.selectedOrder).subscribe(updatedOrder => {
			this.triggerAcrValidation(updatedOrder);
			this.postSavingOrderActions(updatedOrder, avoidRefresh);
		},
			() => {
				this.notificationMessageService.error(this.notificationMessage.FAILED_TO_UPDATE_ORDER);
        this._orderService.setLoader(false);
      });
	}

	/**
	 * @method triggerAcrValidation
	 * @description Triggers ACR Registry validation for the current Order / Event
	 * @param order
	 */
	triggerAcrValidation(order: IOrder) {
		this.isCategoryNeedACRValidation(get(order, 'category.code'))
			.take(1)
			.subscribe(eligibleCategoryForACRValidation => {
				if (this.triggerAcrRequired && eligibleCategoryForACRValidation === true && get(order, 'status.code', null) === 'completed') {
					const orderId = get(order, 'id', null);
					const eventContext: any[] = this.getContextForOrder(order, 'validatedEvent');
					if (!isNull(orderId) && eventContext.length) {
						this._orderService.triggerAcrValidation({ orderId: orderId, eventId: get(eventContext[0], 'reference') });
					} else {
						this._orderService.setLoader(false);
					}
				} else {
					this._orderService.setLoader(false);
				}
				this.changeDetector.markForCheck();
			});
	}

	/**
	 * @method isCategoryNeedACRValidation
	 * @description Check if the selected category needs ACR Registry validation
	 * @param categoryCode
	 * @returns Observable<Boolean>
	 */
	isCategoryNeedACRValidation(categoryCode: string): Observable<boolean> {
		return this._orderService.getOrderDefinition(categoryCode).pipe(
			map(definition => definition.acrValidationRequired)
		);
	}


	postSavingOrderActions(order: IOrder, avoidRefresh?: boolean) {
		this._orderService.updateOrderStore(order);
		if (!avoidRefresh) {
			this._orderService.setSelectedOrder(order);
		}
		this.notificationMessageService.success(this.notificationMessage.ORDER_DETAILS_SAVED_SUCCESSFULLY,10000);
    this._orderService.setLoader(false);

    if (this.selectedOrderDetails && get(this.selectedOrderDetails, '0.category.code') === 'biodesix') {
			this.onBiodesixSftpEvent$.next(true);
		}
		else {
			this.hasUpdatedFields = false;
			this.orderSaveInProgress = false;
		}
	}
	/**
	 * @method saveOrderDetails
	 * @param created
	 * @param updated
	 * @param orderDueDate
	 */
	saveOrderDetails(created: Observable<IOrderDetail[]>, updated: Observable<IOrderDetail[]>, orderDueDate: string) {
		forkJoin(created, updated).pipe(
			map((res) => {
				const jsonResponse = get(res[0], 'body', []);
				return jsonResponse.concat(res[1]);
			})).subscribe(
				async orderDetails => {
					if (orderDetails && orderDetails.length) {
						orderDetails = this.merger(this.selectedOrderDetails, orderDetails, 'id');
						const activeOrderDetailIds = orderDetails.filter(od => od.selected).map(od => od.id);
						if (this.selectedOrder.dueDate != orderDueDate && isEqual(this.selectedOrder.isActive, activeOrderDetailIds)) {
							this.selectedOrder.dueDate = orderDueDate;
						} else {
							set(this.selectedOrder, 'isActive', activeOrderDetailIds);
							let lastSelectedOrderDetail = await this._orderService.getSelectedOrderDetails().pipe(distinctUntilChanged(), take(1)).toPromise();
							this.selectedOrder = this._orderService.findStatusAndDueDate(this.selectedOrder, orderDetails, lastSelectedOrderDetail);
						}
						const formWithValidWorkflowReqId = this._orderService.validateWorkflowRequestId(orderDetails);
						orderDetails = this._orderService.removeUnwantedDataFromOrderDetails(formWithValidWorkflowReqId);
						this._orderService.updateOrderDetailsStore(orderDetails, get(this.selectedOrder, 'category.code', null));
						this.saveOrder(false);
					} else if (this.selectedOrder.dueDate != orderDueDate) {
						this.selectedOrder.dueDate = orderDueDate;
						this.saveOrder(false);
					} else {
						this.setOrderDefinition();
						this._orderService.setLoader(false);
					}
				},
				err => {
					this._orderService.setLoader(false);
					const errorDetails = get(err, 'error.validArray', []);
					let error = '';
					errorDetails.forEach(e => error += get(e, 'message', '') + ' \n ');
					this.notificationMessageService.error(error || this.notificationMessage.FAILED_TO_SAVE_ORDER_DETAILS);
				});
	}



	protected merger(a, b, keyName): IOrderDetail[] {
		var merged = merge(keyBy(a, keyName), keyBy(b, keyName));
		return values(merged) as any;
	}

	/**
	 *
	 * @method getCreateOrderContext
	 * @param currentUser
	 * @description Adds required context's for the order
	 */
	private getCreateOrderContext(currentUser): Array<any> {
		const context = [];
		// Add the contexts required for order
		context.push({ reference: this.patientId, 'resourceType': 'Patient' });
		context.push({ reference: currentUser.id, 'resourceType': 'Practitioner' });
		return context;
	}

	private observeOrderInfoFromStore(): Subscription {
		return this._orderService.getAllOrders().pipe(takeUntil(this.asyncdestroyer.asObservable()))
			.subscribe(async orders => {
				if (this.contributeToTestBarButtonStatus) {
					this.enableDisableTestBarButton(orders);
				}
				if (this.updateStatusDueDateOfDiagnosticOrder) {
					await this.updateDiagnosticOrderAndWorkflowRequest(orders);
				}
			});
	}

	private enableDisableTestBarButton(orders) {
		if (orders.size > 0) {
			let enableCompleteButton: boolean = orders
				.filter(order => get(order, 'status.code') !== 'completed').size === 0;
			this._orderService._aggregatorService.enableDisableTestBarButton(this.constructor.name, enableCompleteButton);
		} else {
			this._orderService._aggregatorService.enableDisableTestBarButton(this.constructor.name, true);
		}
	}

	private async updateDiagnosticOrderAndWorkflowRequest(orders): Promise<any> {
		try {
			let orderInfo = cloneDeep(orders);
			// we are reading diagnostic order from store
			const diagnosticOrder = await this._orderService.getDiagnosticOrderFromStore();
			// derivedStatus is field which will be inserted if not present with upcoming dueDate order
			const diagnosticDerivedStatus = get(diagnosticOrder, 'derivedStatus', null);
			let dueDate: string;

			let orderStatus: { code: string, display: string };
			// When there are orders find the recent dueDate
			if (orderInfo.size > 0) {
				let allOrderNotCompleted: boolean = orderInfo.filter(order => get(order, 'status.code') !== 'completed').size !== 0;
				orderInfo = orderInfo.valueSeq().toArray();
				if (allOrderNotCompleted) {
          orderInfo = orderInfo.filter(ord => ord.dueDate && ord.dueDate.length).sort(this.sortByDueDate(false));
				}
				orderStatus = get(orderInfo, '[0].status');
				dueDate = get(orderInfo, '[0].dueDate');
			} else {
				let status = { code: "", display: "" };
				// We set status as code display so if there are no orders making this empty
				if (!isNull(diagnosticDerivedStatus) && !isEqual(diagnosticDerivedStatus, status)) {
					orderStatus = status;
					dueDate = null;
				} else {
					throw new Error();
				}
			}
			const isStatusNotEqual = !isEqual(diagnosticDerivedStatus, orderStatus);
			const observableArray = [];

			if (!isNull(dueDate) || !isUndefined(dueDate)) {
				const workflowRequest: IWorkflowRequest = await this._orderService.getActiveWorkflowRequestFromStore();
				const activeRevision: IRevision[] = get(workflowRequest, 'revisions').filter(revision => revision.id === workflowRequest.activeRevision);
				const items: IWorkflowJobItem[] = get(activeRevision, '[0].items').filter(items => items.id === workflowRequest.activeJob);
				const diagnosticOrderDueDate = get(items, '[0].dueDate');
				const isDueDateNotEqual = !isEqual(moment(dueDate).format('YYYY-MM-DD'), moment(diagnosticOrderDueDate).format('YYYY-MM-DD'));
				// comparing dueDate in diagnostic is equal to calculated one to stop extra call
				if (dueDate && isDueDateNotEqual) {
					observableArray.push(this.updateWorkflowRequestForDueDate(workflowRequest, dueDate)
						.pipe(map(updatedWorkflowRequest => this._orderService.updateWorkflowActionResourceInStore(Object.assign(workflowRequest, updatedWorkflowRequest)))));
				}
			}

			// comparing status in diagnostic is equal to calculated one to stop extra call
			if (isStatusNotEqual) {
				const body = {
					"entry": [
						{
							"resource": {
								"resourceType": get(diagnosticOrder, 'resourceType'),
								"testType": get(diagnosticOrder, 'testType'),
								'derivedStatus': orderStatus
							},
							"clientSpecificMap": [
								{
									"key": "uuid",
									"value": get(diagnosticOrder, 'id')
								}
							]
						}
					],
					"credentials": {
						"password": "",
						"username": ""
					}
				};
				observableArray.push(this._orderService.updateDiagnosticOrder(body).pipe(map(updatedDiagnosticOrder => {
					set(diagnosticOrder, 'meta', get(updatedDiagnosticOrder, 'meta'));
					set(diagnosticOrder, 'derivedStatus', orderStatus);
					this._orderService.updateCurrentDiagnosticOrderInStore(diagnosticOrder);
				})));
			}
			await forkJoin(...observableArray).pipe(take(1)).toPromise();
		} catch (e) {
		}
		return Promise.resolve();
	}

	private sortByDueDate = (dec) => {
		dec = isUndefined(dec) ? false : dec;
		return (a, b) => {
			if (isNull(get(a, 'dueDate', null))) {
				return 1;
			}
			if (isNull(get(b, 'dueDate', null))) {
				return -1;
			}
			const currentDate = new Date(get(a, 'dueDate'));
			const prevDate = new Date(get(b, 'dueDate'));

			if (currentDate > prevDate) {
				return dec ? -1 : 1;
			}
			if (currentDate < prevDate) {
				return dec ? 1 : -1;
			}
			return 0;
		}
	}

	private updateWorkflowRequestForDueDate(workflowRequest: IWorkflowRequest, dueDate: string): Observable<any> {
		let value = [{
			default: true,
			dueDate: {
				"type": "month",
				"count": "0",
				"actualDueDate": dueDate,
				"resourceIdentifier": ""
			},
			sequenceId: 1
		}];
		let output = [{
			key: "calculateStatusAndDueDate",
			args: "default",
			name: "calculateStatusAndDueDate",
			type: "calculateStatusAndDueDate",
			value: JSON.stringify(value)
		}];
		const payload = {
			workflowId: workflowRequest.id,
			workflowRevisionId: workflowRequest.activeRevision,
			outputs: output,
			action: 'RERUN',
			copyInputsToOutputs: false,
			payload: null,
			removeActiveJobOutputs: true
		};
		return this._orderService.updateWorkflowRequest(payload);
	}
	ngOnDestroy(): void {
		this.asyncdestroyer.next();
		this.asyncdestroyer.complete();
		this._orderService.resetStore();
		this._contextProviderService.clearContexts();
		if (this.orderDataSubscription) {
			this.orderDataSubscription.unsubscribe();
		}
		if (this.contributeToTestBarButtonStatus) {
			this._orderService._aggregatorService.unRegisterToTestBarButton(this.constructor.name);
		}
		this._orderService.setLastEventProcessed(null);
	}
	private checkOrderDetailsRequired(activeOrderDetails:IOrderDetail[]) : boolean{
  	return ((this.selectedOrderDefinition.orderDetailsRequired && activeOrderDetails.length > 0) || !(this.selectedOrderDefinition.orderDetailsRequired) )
  	}
}

